import * as moment from 'moment';
import { ISemanaAgrupada, IRegistroSandiaEncargado } from '../interfaces';

export const obtenerFechaActual = () => moment().format('YYYY-MM-DD');

/**
 * Obtiene el rango de la semana (lunes a domingo) de una fecha
 * @param fecha - Fecha en formato string 'YYYY-MM-DD'
 * @returns Objeto con fechaInicio y fechaFin de la semana
 */
export const obtenerRangoSemana = (fecha: string): { fechaInicio: string; fechaFin: string } => {
  const fechaMoment = moment(fecha);
  const lunes = fechaMoment.clone().startOf('isoWeek'); // Lunes de la semana
  const domingo = fechaMoment.clone().endOf('isoWeek'); // Domingo de la semana

  return {
    fechaInicio: lunes.format('YYYY-MM-DD'),
    fechaFin: domingo.format('YYYY-MM-DD')
  };
};

/**
 * Formatea el rango de semana para mostrar al usuario
 * @param fechaInicio - Fecha de inicio en formato 'YYYY-MM-DD'
 * @param fechaFin - Fecha de fin en formato 'YYYY-MM-DD'
 * @returns String formateado: "29 septiembre a 5 octubre 2025"
 */
export const formatearRangoSemana = (fechaInicio: string, fechaFin: string): string => {
  const inicio = moment(fechaInicio).locale('es');
  const fin = moment(fechaFin).locale('es');

  const diaInicio = inicio.format('D');
  const mesInicio = inicio.format('MMMM');
  const diaFin = fin.format('D');
  const mesFin = fin.format('MMMM');
  const anio = fin.format('YYYY');

  // Si ambas fechas son del mismo mes
  if (mesInicio === mesFin) {
    return `${diaInicio} a ${diaFin} ${mesFin} ${anio}`;
  }

  // Si cruzan meses
  return `${diaInicio} ${mesInicio} a ${diaFin} ${mesFin} ${anio}`;
};

/**
 * Agrupa registros de kilos por semanas
 * @param registros - Array de registros de sandia
 * @returns Array de semanas agrupadas con sus registros y totales
 */
export const agruparRegistrosPorSemanas = (
  registros: Array<IRegistroSandiaEncargado>
): Array<ISemanaAgrupada> => {
  if (!registros || registros.length === 0) {
    return [];
  }

  // Mapa para agrupar por semana (clave: fechaInicio)
  const semanaMap = new Map<string, ISemanaAgrupada>();

  registros.forEach(registro => {
    const { fechaInicio, fechaFin } = obtenerRangoSemana(registro.fecha);

    if (!semanaMap.has(fechaInicio)) {
      semanaMap.set(fechaInicio, {
        rangoSemana: formatearRangoSemana(fechaInicio, fechaFin),
        fechaInicio,
        fechaFin,
        registros: [],
        totalKilogramos: 0,
        totalDinero: 0
      });
    }

    const semana = semanaMap.get(fechaInicio)!;
    semana.registros.push(registro);
    semana.totalKilogramos += parseFloat(registro.kilogramos.toString());
    semana.totalDinero += parseFloat(registro.total.toString());
  });

  // Convertir Map a Array y ordenar por fecha más reciente primero
  return Array.from(semanaMap.values()).sort((a, b) => {
    return moment(b.fechaInicio).valueOf() - moment(a.fechaInicio).valueOf();
  });
};
